/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import type { DesignConfiguration } from '@/stores/designStore';
import type { ComponentCategory, ComponentOption } from '@smartphone-tycoon/shared-core';
import { components } from '@smartphone-tycoon/shared-core';
import { ProductMetricsService } from './ProductMetricsService';
import { GAME_CONFIG } from '../constants/gameConfig';
import { componentReleaseService } from './tycoon/componentReleaseService';

interface AIDesignStrategy {
  pricePoint: 'budget' | 'mainstream' | 'premium' | 'flagship';
  focus: 'performance' | 'camera' | 'battery' | 'value' | 'innovation';
  targetMarket: 'consumer' | 'enterprise' | 'gaming' | 'photography';
}

interface CompetitorProfile {
  id: string;
  innovationFocus: number; // 0-1
  priceCompetitiveness: number; // 0-1
  preferredSegments: string[];
}

export class AIDesignGeneratorService {
  private static instance: AIDesignGeneratorService;
  private gameYear: number = GAME_CONFIG.STARTING_YEAR; // Default to start year

  private constructor() {}

  public static getInstance(): AIDesignGeneratorService {
    if (!AIDesignGeneratorService.instance) {
      AIDesignGeneratorService.instance = new AIDesignGeneratorService();
    }
    return AIDesignGeneratorService.instance;
  }

  /**
   * Generate a realistic design configuration for AI competitors
   */
  public generateDesignConfiguration(
    competitorProfile: CompetitorProfile,
    marketConstraints?: {
      playerAverageTechScore?: number;
      playerAveragePrice?: number;
      emergingTrends?: string[];
      playerProducts?: Array<{ price: number; techScore: number; marketShare?: number }>;
      competitorProducts?: Array<{ price: number; techScore: number; competitorId: string }>;
      marketSaturation?: number;
      economicConditions?: number;
    }
  ): DesignConfiguration {
    // Determine AI design strategy based on competitor profile
    const strategy = this.determineDesignStrategy(competitorProfile, marketConstraints);
    
    // Generate component selection based on strategy
    const selectedComponents = this.selectComponentsForStrategy(strategy, competitorProfile);
    
    // Generate realistic design properties
    const designProperties = this.generateDesignProperties(strategy, competitorProfile);
    
    // Generate product name and pricing with market context
    const marketContext = marketConstraints ? {
      playerProducts: marketConstraints.playerProducts,
      competitorProducts: marketConstraints.competitorProducts,
      marketSaturation: marketConstraints.marketSaturation,
      economicConditions: marketConstraints.economicConditions
    } : undefined;
    
    const productInfo = this.generateProductInfo(strategy, selectedComponents, competitorProfile.id, marketContext);

    return {
      ...designProperties,
      ...selectedComponents,
      ...productInfo,
      logo: {
        type: 'none',
        scale: 0.2,
        position: { x: 0, y: 0 },
        rotation: 0,
      },
    };
  }

  /**
   * Generate multiple design variants for A/B testing
   */
  public generateDesignVariants(
    competitorProfile: CompetitorProfile,
    count: number = 3
  ): DesignConfiguration[] {
    const variants: DesignConfiguration[] = [];
    
    for (let i = 0; i < count; i++) {
      // Add randomness to create different variants
      const modifiedProfile = {
        ...competitorProfile,
        innovationFocus: this.addRandomness(competitorProfile.innovationFocus, 0.1),
        priceCompetitiveness: this.addRandomness(competitorProfile.priceCompetitiveness, 0.1),
      };
      
      variants.push(this.generateDesignConfiguration(modifiedProfile));
    }
    
    return variants;
  }

  /**
   * Generate design that responds to player actions
   */
  public generateCompetitiveResponse(
    competitorProfile: CompetitorProfile,
    playerProduct: {
      techScore: number;
      price: number;
      components: Record<string, string>;
    },
    marketReception: number
  ): DesignConfiguration {
    // Analyze player product to determine response strategy
    const responseStrategy = this.analyzePlayerProductForResponse(
      playerProduct,
      marketReception,
      competitorProfile
    );

    // Generate design that addresses player's strengths/weaknesses
    const config = this.generateDesignConfiguration(competitorProfile, {
      playerAverageTechScore: playerProduct.techScore,
      playerAveragePrice: playerProduct.price,
      emergingTrends: this.identifyPlayerTrends(playerProduct.components),
    });
    
    // For competitive responses, ensure price is within reasonable range of player's price
    if (competitorProfile.priceCompetitiveness > 0.5) {
      const maxPriceDiff = playerProduct.price * 0.4; // Max 40% difference
      if (Math.abs(config.price - playerProduct.price) > maxPriceDiff) {
        // Adjust price to be within range
        if (config.price > playerProduct.price) {
          config.price = Math.round(playerProduct.price + maxPriceDiff);
        } else {
          config.price = Math.round(playerProduct.price - maxPriceDiff);
        }
      }
    }
    
    return config;
  }

  private determineDesignStrategy(
    competitorProfile: CompetitorProfile,
    marketConstraints?: any
  ): AIDesignStrategy {
    // Determine price point based on segments and competitiveness
    let pricePoint: AIDesignStrategy['pricePoint'] = 'mainstream';
    
    // Prioritize budget if it's in preferred segments
    if (competitorProfile.preferredSegments.includes('budget')) {
      pricePoint = 'budget';
    } else if (competitorProfile.preferredSegments.includes('premium') || 
        competitorProfile.preferredSegments.includes('flagship')) {
      pricePoint = competitorProfile.priceCompetitiveness > 0.7 ? 'premium' : 'flagship';
    }

    // Determine focus based on innovation and market constraints
    let focus: AIDesignStrategy['focus'] = 'value';
    
    if (competitorProfile.innovationFocus > 0.8) {
      focus = 'innovation';
    } else if (competitorProfile.innovationFocus > 0.6) {
      focus = Math.random() > 0.5 ? 'performance' : 'camera';
    } else if (competitorProfile.priceCompetitiveness > 0.7) {
      focus = 'value';
    } else {
      focus = 'battery'; // Safe, practical choice
    }

    // Determine target market
    let targetMarket: AIDesignStrategy['targetMarket'] = 'consumer';
    
    if (competitorProfile.preferredSegments.includes('enterprise')) {
      targetMarket = 'enterprise';
    } else if (focus === 'performance') {
      targetMarket = 'gaming';
    } else if (focus === 'camera') {
      targetMarket = 'photography';
    }

    return { pricePoint, focus, targetMarket };
  }

  private selectComponentsForStrategy(
    strategy: AIDesignStrategy,
    competitorProfile: CompetitorProfile
  ): Pick<DesignConfiguration, 'os' | 'cpu' | 'gpu' | 'ram' | 'storage' | 'network' | 'camera' | 'battery'> {
    const selectedComponents: any = {};

    // Define component tier preferences based on strategy
    const tierPreferences = this.getTierPreferences(strategy);

    components.forEach(category => {
      const preferredTier = tierPreferences[category.id] || 'mid';
      const component = this.selectComponentByTier(category, preferredTier, competitorProfile);
      if (component) {
        selectedComponents[category.id] = component.value;
      }
    });

    return selectedComponents;
  }

  private getTierPreferences(strategy: AIDesignStrategy): Record<string, 'low' | 'mid' | 'high'> {
    const basePreferences: Record<string, 'low' | 'mid' | 'high'> = {
      os: 'mid',
      cpu: 'mid',
      gpu: 'mid',
      ram: 'mid',
      storage: 'mid',
      network: 'mid',
      camera: 'mid',
      battery: 'mid',
    };

    // Adjust based on price point - MORE price-conscious selection
    if (strategy.pricePoint === 'budget') {
      // Budget phones: Strongly prefer low-tier components
      Object.keys(basePreferences).forEach(key => {
        basePreferences[key] = Math.random() > 0.15 ? 'low' : 'mid'; // 85% low, 15% mid
      });
    } else if (strategy.pricePoint === 'mainstream') {
      // Mainstream: Mix of low and mid, rarely high
      Object.keys(basePreferences).forEach(key => {
        const rand = Math.random();
        basePreferences[key] = rand > 0.7 ? 'mid' : 'low'; // 30% mid, 70% low
      });
    } else if (strategy.pricePoint === 'premium') {
      // Premium: Mix of mid and high
      Object.keys(basePreferences).forEach(key => {
        basePreferences[key] = Math.random() > 0.4 ? 'high' : 'mid'; // 60% high, 40% mid
      });
    } else if (strategy.pricePoint === 'flagship') {
      Object.keys(basePreferences).forEach(key => {
        basePreferences[key] = Math.random() > 0.2 ? 'high' : 'mid'; // 80% high, 20% mid
      });
    }

    // Adjust based on focus
    switch (strategy.focus) {
      case 'performance':
        basePreferences.cpu = 'high';
        basePreferences.gpu = 'high';
        basePreferences.ram = 'high';
        break;
      case 'camera':
        basePreferences.camera = 'high';
        basePreferences.gpu = 'high'; // For image processing
        break;
      case 'battery':
        basePreferences.battery = 'high';
        // Optimize other components for efficiency
        basePreferences.cpu = basePreferences.cpu === 'high' ? 'mid' : basePreferences.cpu;
        break;
      case 'innovation':
        // Mix of high-end components
        basePreferences.os = 'high';
        basePreferences.network = 'high';
        basePreferences.cpu = 'high';
        break;
      case 'value':
        // Balanced approach - avoid extremes
        Object.keys(basePreferences).forEach(key => {
          if (basePreferences[key] === 'low') basePreferences[key] = 'mid';
        });
        break;
    }

    return basePreferences;
  }

  private selectComponentByTier(
    category: ComponentCategory,
    preferredTier: 'low' | 'mid' | 'high',
    competitorProfile: CompetitorProfile
  ) {
    // Get available components based on current game year
    const availableComponents = componentReleaseService.getAvailableComponents(
      category.id,
      this.gameYear
    );

    if (availableComponents.length === 0) {
      // Fallback to base components if none available
      return category.options[0];
    }

    const sortedOptions = [...availableComponents].sort((a, b) => a.techScore - b.techScore);
    const sortedByCost = [...availableComponents].sort((a, b) => a.cost - b.cost);
    
    // Price-conscious companies should prioritize cost over tech score
    const priceCompetitiveness = competitorProfile.priceCompetitiveness || 0.5;
    const isPriceConscious = priceCompetitiveness > 0.7;
    
    // For low tier preference, ALWAYS prioritize cost
    if (preferredTier === 'low') {
      // Use cost-sorted array for low tier
      const maxCostIndex = Math.floor(sortedByCost.length * 0.3); // Bottom 30% by cost
      
      // Very price-conscious competitors (Nokia, LG, Motorola) pick cheapest
      if (isPriceConscious || competitorProfile.id === 'nokia' || 
          competitorProfile.id === 'lg' || competitorProfile.id === 'motorola') {
        return sortedByCost[0]; // Absolute cheapest
      }
      
      // Others pick from bottom 30% by cost
      const index = Math.floor(Math.random() * maxCostIndex);
      return sortedByCost[index];
    }
    
    let targetIndex: number;
    
    switch (preferredTier) {
      case 'high':
        targetIndex = sortedOptions.length - 1;
        break;
      case 'mid':
      default:
        // For mid-tier, consider price competitiveness
        if (isPriceConscious) {
          // Price-conscious companies pick lower-mid tier
          targetIndex = Math.floor(sortedOptions.length * 0.35);
        } else {
          targetIndex = Math.floor(sortedOptions.length / 2);
        }
        break;
    }

    // Add some randomness based on innovation focus
    const randomness = (1 - competitorProfile.innovationFocus) * 0.3;
    const variance = Math.floor(randomness * sortedOptions.length);
    
    const minIndex = Math.max(0, targetIndex - variance);
    const maxIndex = Math.min(sortedOptions.length - 1, targetIndex + variance);
    
    const actualIndex = Math.floor(Math.random() * (maxIndex - minIndex + 1)) + minIndex;
    
    return sortedOptions[actualIndex];
  }

  private generateDesignProperties(
    strategy: AIDesignStrategy,
    competitorProfile: CompetitorProfile
  ): Pick<DesignConfiguration, 'color' | 'material' | 'buttonColor' | 'hasButtons' | 'screenBorderColor' | 'cameraStyle' | 'cameraColor' | 'cameraPosition' | 'buttonPositions' | 'phoneSize' | 'screenSize' | 'cornerRadius'> {
    // Generate design based on target market and strategy
    const colors = this.getColorsForStrategy(strategy);
    const materials = this.getMaterialsForStrategy(strategy);
    
    const selectedColor = colors[Math.floor(Math.random() * colors.length)];
    const selectedMaterial = materials[Math.floor(Math.random() * materials.length)];

    return {
      color: selectedColor,
      material: selectedMaterial,
      buttonColor: 'match',
      hasButtons: true,
      screenBorderColor: 'match',
      cameraStyle: this.getCameraStyleForStrategy(strategy),
      cameraColor: '#000000',
      cameraPosition: this.generateCameraPosition(strategy),
      buttonPositions: { volume: Math.random() * 0.4 - 0.2, power: Math.random() * 0.4 - 0.2 },
      phoneSize: this.generatePhoneSize(strategy),
      screenSize: this.generateScreenSize(strategy),
      cornerRadius: this.generateCornerRadius(strategy),
    };
  }

  private generateProductInfo(
    strategy: AIDesignStrategy,
    selectedComponents: Record<string, string>,
    competitorId?: string,
    marketContext?: {
      playerProducts?: Array<{ price: number; techScore: number; marketShare?: number }>;
      competitorProducts?: Array<{ price: number; techScore: number; competitorId: string }>;
      marketSaturation?: number;
      economicConditions?: number;
    }
  ): Pick<DesignConfiguration, 'name' | 'price'> {
    // Calculate tech score and base price using ProductMetricsService
    const techScore = ProductMetricsService.calculateTechScore(selectedComponents);
    const baseCost = ProductMetricsService.calculateTotalCost(selectedComponents);
    
    // Generate price based on strategy, tech score, and market context
    const price = this.calculateAIPrice(strategy, baseCost, techScore, competitorId, marketContext);
    
    // Generate product name - use brand-specific names if competitor ID provided
    const name = this.generateProductName(strategy, competitorId);

    return { name, price };
  }

  private getColorsForStrategy(strategy: AIDesignStrategy): string[] {
    const colorPalettes = {
      budget: ['#ffffff', '#000000', '#4a5568', '#2d3748'],
      mainstream: ['#ffffff', '#000000', '#3182ce', '#38a169', '#e53e3e'],
      premium: ['#ffffff', '#000000', '#2d3748', '#4a5568', '#8b5cf6'],
      flagship: ['#ffffff', '#000000', '#1a202c', '#2d3748', '#4a5568', '#718096'],
    };

    return colorPalettes[strategy.pricePoint] || colorPalettes.mainstream;
  }

  private getMaterialsForStrategy(strategy: AIDesignStrategy): string[] {
    const materialOptions = {
      budget: ['plastic', 'aluminum'],
      mainstream: ['plastic', 'aluminum'],
      premium: ['aluminum'],
      flagship: ['aluminum'],
    };

    return materialOptions[strategy.pricePoint] || materialOptions.mainstream;
  }

  private getCameraStyleForStrategy(strategy: AIDesignStrategy): string {
    const cameraStyles = ['pill', 'square', 'circular'];
    
    if (strategy.focus === 'camera') {
      return 'square'; // More professional look
    } else if (strategy.focus === 'innovation') {
      return 'circular'; // Distinctive design
    }
    
    return cameraStyles[Math.floor(Math.random() * cameraStyles.length)];
  }

  private generateCameraPosition(strategy: AIDesignStrategy): { x: number; y: number } {
    // Generate realistic camera positions
    const positions = [
      { x: -0.3, y: 0.7 }, // Top left
      { x: 0, y: 0.7 },    // Top center
      { x: 0.3, y: 0.7 },  // Top right
    ];
    
    return positions[Math.floor(Math.random() * positions.length)];
  }

  private generatePhoneSize(strategy: AIDesignStrategy): { width: number; height: number; depth: number } {
    const baseSizes = {
      budget: { width: 2.3, height: 4.8, depth: 0.35 },
      mainstream: { width: 2.5, height: 5.0, depth: 0.3 },
      premium: { width: 2.6, height: 5.2, depth: 0.28 },
      flagship: { width: 2.7, height: 5.4, depth: 0.25 },
    };

    const baseSize = baseSizes[strategy.pricePoint];
    
    // Add slight random variation
    return {
      width: baseSize.width + (Math.random() - 0.5) * 0.2,
      height: baseSize.height + (Math.random() - 0.5) * 0.3,
      depth: baseSize.depth + (Math.random() - 0.5) * 0.05,
    };
  }

  private generateScreenSize(strategy: AIDesignStrategy): { width: number; height: number } {
    const screenPercentages = {
      budget: { width: 85, height: 90 },
      mainstream: { width: 88, height: 92 },
      premium: { width: 90, height: 94 },
      flagship: { width: 92, height: 96 },
    };

    const base = screenPercentages[strategy.pricePoint];
    
    return {
      width: base.width + Math.random() * 4 - 2,
      height: base.height + Math.random() * 4 - 2,
    };
  }

  private generateCornerRadius(strategy: AIDesignStrategy): number {
    const radiusRanges = {
      budget: [0.1, 0.25],
      mainstream: [0.15, 0.3],
      premium: [0.2, 0.35],
      flagship: [0.25, 0.4],
    };

    const [min, max] = radiusRanges[strategy.pricePoint];
    return min + Math.random() * (max - min);
  }

  private calculateAIPrice(
    strategy: AIDesignStrategy,
    baseCost: number,
    techScore: number,
    competitorId?: string,
    marketContext?: {
      playerProducts?: Array<{ price: number; techScore: number; marketShare?: number }>;
      competitorProducts?: Array<{ price: number; techScore: number; competitorId: string }>;
      marketSaturation?: number;
      economicConditions?: number; // 0-1, where 1 is excellent
    }
  ): number {
    // Base margin multipliers by price point
    const baseMarginMultipliers = {
      budget: 1.12,     // 12% margin (reduced for more competitive pricing)
      mainstream: 1.22, // 22% margin
      premium: 1.35,    // 35% margin
      flagship: 1.5,    // 50% margin
    };

    // Get competitor profile if available
    const competitorProfile = competitorId ? this.getCompetitorPricingProfile(competitorId) : null;
    
    // Adjust margin based on competitor's pricing strategy
    let marginMultiplier = baseMarginMultipliers[strategy.pricePoint];
    
    if (competitorProfile) {
      // Budget segment: All competitors are aggressive
      if (strategy.pricePoint === 'budget') {
        if (competitorId === 'nokia') {
          marginMultiplier *= 0.65; // Nokia: Most aggressive (7.8% margin)
        } else if (competitorId === 'lg' || competitorId === 'motorola') {
          marginMultiplier *= 0.75; // LG/Motorola: Very aggressive (9% margin)
        } else if (competitorId === 'samsung') {
          marginMultiplier *= 0.8; // Samsung: Aggressive volume play (9.6% margin)
        } else if (competitorId === 'apple') {
          marginMultiplier *= 0.9; // Apple: Less aggressive but still competitive (10.8% margin)
        } else {
          marginMultiplier *= 0.85; // Other competitors: Competitive (10.2% margin)
        }
      }
      // Mainstream segment: Moderately aggressive pricing
      else if (strategy.pricePoint === 'mainstream') {
        if (competitorId === 'nokia') {
          marginMultiplier *= 0.75; // Nokia: Still very aggressive (16.5% margin)
        } else if (competitorId === 'lg' || competitorId === 'motorola') {
          marginMultiplier *= 0.85; // LG/Motorola: Aggressive (18.7% margin)
        } else if (competitorId === 'samsung') {
          marginMultiplier *= 0.9; // Samsung: Competitive (19.8% margin)
        } else if (competitorId === 'apple') {
          marginMultiplier *= 1.0; // Apple: Standard margins (22% margin)
        } else {
          marginMultiplier *= 0.9; // Other competitors: Competitive (19.8% margin)
        }
      }
      // Premium/Flagship segments: Keep existing strategy
      else {
        if (competitorId === 'samsung') {
          marginMultiplier *= 0.95; // Samsung: Slightly lower margins for volume
        } else if (competitorId === 'apple') {
          marginMultiplier *= 1.1; // Apple: Higher margins for brand premium
        }
      }
    }

    // Calculate base price with margin
    let price = baseCost * marginMultiplier;
    
    // Tech score adjustment (more significant than before)
    const techAdjustment = this.calculateTechPriceAdjustment(techScore, strategy.pricePoint);
    price *= techAdjustment;
    
    // Market context adjustments
    if (marketContext) {
      // Competitive pricing based on existing products
      if (marketContext.playerProducts && marketContext.playerProducts.length > 0) {
        const competitivePriceAdjustment = this.calculateCompetitivePricing(
          price, 
          techScore, 
          strategy.pricePoint,
          marketContext.playerProducts,
          competitorProfile?.priceAggressiveness || 0.5
        );
        price = competitivePriceAdjustment;
      }
      
      // Market saturation adjustment - INCREASED IMPACT
      if (marketContext.marketSaturation !== undefined) {
        const saturationMultiplier = 1 - (marketContext.marketSaturation * 0.3); // Up to 30% reduction in saturated markets
        price *= saturationMultiplier;
      }
      
      // Economic conditions adjustment - INCREASED IMPACT
      if (marketContext.economicConditions !== undefined) {
        const economicMultiplier = 0.85 + (marketContext.economicConditions * 0.3); // ±15% based on economy
        price *= economicMultiplier;
      }
      
      // Additional competitor density adjustment
      if (marketContext.competitorProducts && marketContext.competitorProducts.length > 2) {
        // More competitors = more price pressure
        const competitorCount = marketContext.competitorProducts.length;
        const densityMultiplier = Math.max(0.85, 1 - (competitorCount * 0.03)); // 3% reduction per competitor, max 15%
        price *= densityMultiplier;
      }
    }
    
    // Product lifecycle pricing (if this is a refresh/update)
    const lifecyclePricing = this.getLifecyclePricing(competitorId, strategy);
    price *= lifecyclePricing;
    
    // Final market positioning variance
    const marketVariance = 0.95 + Math.random() * 0.1; // ±5% variance (reduced from ±10%)
    price *= marketVariance;
    
    // Ensure minimum profit margin
    const minimumPrice = baseCost * 1.05; // At least 5% margin
    price = Math.max(price, minimumPrice);
    
    // Round to realistic price points
    return this.roundToRealisticPrice(price, strategy.pricePoint);
  }
  
  private getCompetitorPricingProfile(competitorId: string): { priceAggressiveness: number } | null {
    const profiles: Record<string, { priceAggressiveness: number }> = {
      nokia: { priceAggressiveness: 0.9 }, // Very aggressive
      samsung: { priceAggressiveness: 0.7 }, // Aggressive
      google: { priceAggressiveness: 0.5 }, // Moderate
      apple: { priceAggressiveness: 0.3 }, // Conservative
      lg: { priceAggressiveness: 0.6 },
      motorola: { priceAggressiveness: 0.65 },
      palm: { priceAggressiveness: 0.8 },
      microsoft: { priceAggressiveness: 0.55 },
      rim: { priceAggressiveness: 0.4 }
    };
    
    return profiles[competitorId] || null;
  }
  
  private calculateTechPriceAdjustment(techScore: number, pricePoint: string): number {
    // More significant tech score impact based on segment
    const techImpactBySegment = {
      budget: 0.08,      // 8% max impact (reduced from 15%)
      mainstream: 0.15,  // 15% max impact (reduced from 25%)
      premium: 0.25,     // 25% max impact (reduced from 35%)
      flagship: 0.35     // 35% max impact (reduced from 45%)
    };
    
    const maxImpact = techImpactBySegment[pricePoint as keyof typeof techImpactBySegment] || 0.15;
    const normalizedTech = (techScore - 50) / 50; // Normalize to -1 to 1 range
    return 1 + (normalizedTech * maxImpact);
  }
  
  private calculateCompetitivePricing(
    basePrice: number,
    techScore: number,
    pricePoint: string,
    competingProducts: Array<{ price: number; techScore: number; marketShare?: number }>,
    aggressiveness: number
  ): number {
    // Find products in similar tech range
    const techRange = 15; // Consider products within 15 tech score points
    const similarProducts = competingProducts.filter(p => 
      Math.abs(p.techScore - techScore) <= techRange
    );
    
    if (similarProducts.length === 0) return basePrice;
    
    // Calculate weighted average price (weighted by market share if available)
    const totalWeight = similarProducts.reduce((sum, p) => sum + (p.marketShare || 1), 0);
    const weightedAvgPrice = similarProducts.reduce((sum, p) => 
      sum + p.price * (p.marketShare || 1), 0
    ) / totalWeight;
    
    // Determine competitive position based on aggressiveness
    let targetPrice: number;
    if (aggressiveness > 0.8) {
      // Very Aggressive: Undercut by 10-20%
      targetPrice = weightedAvgPrice * (0.8 + Math.random() * 0.1);
    } else if (aggressiveness > 0.6) {
      // Aggressive: Undercut by 5-15%
      targetPrice = weightedAvgPrice * (0.85 + Math.random() * 0.1);
    } else if (aggressiveness > 0.4) {
      // Moderate: Match within ±7%
      targetPrice = weightedAvgPrice * (0.93 + Math.random() * 0.14);
    } else {
      // Conservative: Price at or slightly above (0-5%)
      targetPrice = weightedAvgPrice * (1 + Math.random() * 0.05);
    }
    
    // Blend with base price - increase competitive influence for budget/mainstream
    const blendFactor = (pricePoint === 'budget' || pricePoint === 'mainstream') ? 0.8 : 0.7;
    return basePrice * (1 - blendFactor) + targetPrice * blendFactor;
  }
  
  private getLifecyclePricing(competitorId?: string, strategy?: AIDesignStrategy): number {
    if (!competitorId || !strategy) return 1.0;
    
    const gameYear = this.getCurrentGameYear();
    const yearsSinceStart = gameYear - GAME_CONFIG.STARTING_YEAR;
    
    // Special handling for companies with market exit strategies
    if (competitorId === 'nokia' && yearsSinceStart > 10) {
      // Nokia struggles in smartphone era, more aggressive pricing
      return 0.85;
    } else if (competitorId === 'palm' && yearsSinceStart > 5) {
      // Palm's decline phase
      return 0.9;
    } else if (competitorId === 'rim' && yearsSinceStart > 8) {
      // BlackBerry's decline
      return 0.88;
    }
    
    // Standard lifecycle pricing
    if (yearsSinceStart < 2) {
      return 1.05; // Early market premium
    } else if (yearsSinceStart > 15) {
      return 0.95; // Mature market discounting
    }
    
    return 1.0;
  }
  
  private roundToRealisticPrice(price: number, pricePoint: string): number {
    // Define price caps based on market segments (from gameConstants)
    const priceCaps = {
      budget: { min: 200, max: 350 },     // Slightly higher max for some flexibility
      mainstream: { min: 400, max: 750 }, // Slightly higher max for some flexibility
      premium: { min: 800, max: 1300 },   // Slightly higher max for some flexibility
      flagship: { min: 1200, max: 2000 }  // Flagship can go higher
    };
    
    // Apply price caps to ensure competitive pricing
    const caps = priceCaps[pricePoint as keyof typeof priceCaps];
    if (caps) {
      price = Math.max(caps.min, Math.min(caps.max, price));
    }
    
    // Round to realistic price points based on segment
    const roundingRules = {
      budget: 10,      // Round to nearest $10
      mainstream: 25,  // Round to nearest $25
      premium: 50,     // Round to nearest $50
      flagship: 100    // Round to nearest $100
    };
    
    const rounding = roundingRules[pricePoint as keyof typeof roundingRules] || 25;
    
    // For budget phones, always round down to emphasize value
    if (pricePoint === 'budget') {
      return Math.floor(price / rounding) * rounding;
    }
    
    const rounded = Math.round(price / rounding) * rounding;
    
    // Ensure common pricing patterns (e.g., $X99)
    if (pricePoint === 'premium' || pricePoint === 'flagship') {
      return rounded - 1; // e.g., $999 instead of $1000
    }
    
    return rounded;
  }

  private generateProductName(strategy: AIDesignStrategy, competitorId?: string): string {
    // Generate brand-specific names for known competitors
    if (competitorId) {
      switch (competitorId) {
        case 'apple':
          return this.generateAppleProductName(strategy);
        case 'samsung':
          return this.generateSamsungProductName(strategy);
        case 'google':
          return this.generateGoogleProductName(strategy);
        case 'nokia':
          return this.generateNokiaProductName(strategy);
      }
    }
    
    // Fallback to generic naming for unknown competitors
    const prefixes = {
      budget: ['Essential', 'Core', 'Basic', 'Start', 'Entry'],
      mainstream: ['Pro', 'Plus', 'Advanced', 'Elite', 'Prime'],
      premium: ['Ultra', 'Max', 'Supreme', 'Flagship', 'Platinum'],
      flagship: ['Quantum', 'Apex', 'Ultimate', 'Vision', 'Infinity'],
    };

    const suffixes = ['X', 'Pro', 'Plus', 'Elite', 'Max', 'Ultra', '5G', 'AI'];
    const numbers = Array.from({ length: 20 }, (_, i) => i + 10); // 10-29

    const prefix = prefixes[strategy.pricePoint][Math.floor(Math.random() * prefixes[strategy.pricePoint].length)];
    const number = numbers[Math.floor(Math.random() * numbers.length)];
    const suffix = Math.random() > 0.4 ? suffixes[Math.floor(Math.random() * suffixes.length)] : '';

    return `${prefix} ${number}${suffix ? ' ' + suffix : ''}`.trim();
  }
  
  private generateAppleProductName(strategy: AIDesignStrategy): string {
    // iPhone naming pattern: first release has no number, then starts from 2
    const gameYear = this.getCurrentGameYear();
    const yearsSinceStart = gameYear - GAME_CONFIG.STARTING_YEAR;
    
    const variants = ['', ' Plus', ' Pro', ' Pro Max'];
    const variant = strategy.pricePoint === 'flagship' ? ' Pro Max' :
                   strategy.pricePoint === 'premium' ? ' Pro' :
                   strategy.pricePoint === 'mainstream' ? ' Plus' : '';
    
    if (yearsSinceStart === 0) {
      return `iPhone${variant}`;
    } else {
      const iPhoneNumber = yearsSinceStart + 1;
      return `iPhone ${iPhoneNumber}${variant}`;
    }
  }
  
  private generateSamsungProductName(strategy: AIDesignStrategy): string {
    // Galaxy naming pattern: first release has no number, then starts from 2
    const gameYear = this.getCurrentGameYear();
    const yearsSinceStart = gameYear - GAME_CONFIG.STARTING_YEAR;
    
    const foldStartYear = GAME_CONFIG.STARTING_YEAR + 5;
    if (strategy.pricePoint === 'flagship' && gameYear >= foldStartYear && Math.random() > 0.5) {
      // Sometimes generate Galaxy Fold (starts 5 years after beginning)
      const yearsSinceFoldStart = gameYear - foldStartYear;
      if (yearsSinceFoldStart === 0) {
        return 'Galaxy Z Fold';
      } else {
        const foldNumber = yearsSinceFoldStart + 1;
        return `Galaxy Z Fold${foldNumber}`;
      }
    }
    
    const variants = ['', ' Plus', ' Ultra'];
    const variant = strategy.pricePoint === 'flagship' ? ' Ultra' :
                   strategy.pricePoint === 'premium' ? ' Plus' : '';
    
    if (yearsSinceStart === 0) {
      return `Galaxy S${variant}`;
    } else {
      const galaxyNumber = yearsSinceStart + 1;
      return `Galaxy S${galaxyNumber}${variant}`;
    }
  }
  
  private generateGoogleProductName(strategy: AIDesignStrategy): string {
    // Google naming pattern: Nexus first 10 years, then Pixel
    const gameYear = this.getCurrentGameYear();
    const nexusEndYear = GAME_CONFIG.STARTING_YEAR + 10;
    
    if (gameYear < nexusEndYear) {
      // Nexus era: first 10 years
      const yearsSinceStart = gameYear - GAME_CONFIG.STARTING_YEAR;
      if (yearsSinceStart === 0) {
        return 'Nexus';
      } else {
        const nexusNumber = yearsSinceStart + 1;
        return `Nexus ${nexusNumber}`;
      }
    }
    
    // Pixel era (after 10 years)
    const yearsSincePixelStart = gameYear - nexusEndYear;
    const variants = ['', ' Pro', ' XL'];
    const variant = strategy.pricePoint === 'flagship' ? ' Pro' :
                   strategy.pricePoint === 'premium' ? ' XL' : '';
    
    if (yearsSincePixelStart === 0) {
      return `Pixel${variant}`;
    } else {
      const pixelNumber = yearsSincePixelStart + 1;
      return `Pixel ${pixelNumber}${variant}`;
    }
  }
  
  private generateNokiaProductName(strategy: AIDesignStrategy): string {
    // Nokia naming pattern: N-series for high-end, numbered series for budget/mainstream
    const gameYear = this.getCurrentGameYear();
    const yearsSinceStart = gameYear - GAME_CONFIG.STARTING_YEAR;
    
    if (strategy.pricePoint === 'flagship' || strategy.pricePoint === 'premium') {
      // N-series for premium phones (N95, N97, etc.)
      const nSeriesNumbers = [70, 73, 75, 78, 80, 82, 85, 86, 90, 93, 95, 96, 97, 900];
      const index = Math.min(yearsSinceStart, nSeriesNumbers.length - 1);
      return `N${nSeriesNumbers[index]}`;
    } else if (strategy.pricePoint === 'mainstream') {
      // E-series for business/mainstream (E71, E72, etc.)
      const baseNumber = 50 + (yearsSinceStart * 3);
      return `E${baseNumber}`;
    } else {
      // Budget phones: Classic numbered series (3310, 5230, etc.)
      const series = [1000, 2000, 3000, 5000, 6000, 7000];
      const seriesBase = series[Math.floor(Math.random() * series.length)];
      const model = Math.floor(Math.random() * 500) + 100;
      return `${seriesBase + model}`;
    }
  }

  private addRandomness(value: number, variance: number): number {
    const change = (Math.random() - 0.5) * 2 * variance;
    return Math.max(0, Math.min(1, value + change));
  }

  private analyzePlayerProductForResponse(
    playerProduct: any,
    marketReception: number,
    competitorProfile: CompetitorProfile
  ): AIDesignStrategy {
    // Determine competitive response strategy
    let responseType: 'direct_compete' | 'differentiate' | 'undercut' | 'premium_alternative' = 'direct_compete';
    
    if (marketReception > 0.7) {
      // Player product is successful
      if (competitorProfile.priceCompetitiveness > 0.7) {
        responseType = 'undercut';
      } else if (competitorProfile.innovationFocus > 0.7) {
        responseType = 'premium_alternative';
      } else {
        responseType = 'differentiate';
      }
    }

    // Convert to design strategy
    return this.determineDesignStrategy(competitorProfile, {
      playerAverageTechScore: playerProduct.techScore,
      playerAveragePrice: playerProduct.price,
    });
  }

  private identifyPlayerTrends(playerComponents: Record<string, string>): string[] {
    const trends: string[] = [];
    
    // Analyze player component choices for trends
    Object.entries(playerComponents).forEach(([category, component]) => {
      const categoryData = components.find(c => c.id === category);
      const componentData = categoryData?.options.find(o => o.value === component);
      
      if (componentData?.techScore > 85) {
        trends.push(`high_end_${category}`);
      }
    });

    return trends;
  }

  /**
   * Set the current game year for proper product naming
   */
  public setGameYear(year: number): void {
    this.gameYear = year;
    // Trigger component releases for the year
    componentReleaseService.processYearlyReleases(year);
  }

  /**
   * Get the current game year
   */
  private getCurrentGameYear(): number {
    return this.gameYear;
  }

  /**
   * Reset the service state
   */
  public reset(): void {
    this.gameYear = GAME_CONFIG.STARTING_YEAR;
  }
}

export const aiDesignGenerator = AIDesignGeneratorService.getInstance();